import React from "react";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { Link } from "react-router-dom";
import "./styles.scss";
import { getPresignedUrlView } from "../../utilities/files";

function NewsComponent({ blogPageWithSize, getBlogPageWithSize, nextPage, loading, error }) {
  const [presignedUrls, setPresignedUrls] = useState({}); // Store URLs for each blog
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleBlogs, setVisibleBlogs] = useState(new Set());
  const [preloadedPages, setPreloadedPages] = useState(new Set());
  const [preloadedArticles, setPreloadedArticles] = useState(new Set());
  const observerRef = useRef(null);
  const blogCardsRef = useRef(new Map());
  

  

  // Helper function to check if a URL is from S3
  const isS3Url = useCallback((url) => {
    return url?.startsWith("https://amarant2.s3") || 
           url?.startsWith("https://amarant-rpa.s3") || 
           url?.startsWith("https://amarant-docs.s3");
  }, []);

  // Article preloading on hover
  const preloadArticle = useCallback(async (blogId, coverPhoto) => {
    if (preloadedArticles.has(blogId)) return;
    
    try {
      // Preload article image if it's an S3 URL
      if (coverPhoto && isS3Url(coverPhoto)) {
        await getPresignedUrlView(coverPhoto);
        
        
      }
      
      setPreloadedArticles(prev => new Set([...prev, blogId]));
    } catch (error) {
      
    }
  }, [preloadedArticles, isS3Url]);

  // Preloading strategy - preload next page when user scrolls 70%
  const handlePreloading = useCallback(() => {
    const scrollPercent = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
    
    
    
    // Preload next page at 70% scroll
    if (scrollPercent > 70 && nextPage && !preloadedPages.has(nextPage) && !loading && !loadingMore) {
      setPreloadedPages(prev => new Set([...prev, nextPage]));
      
      
    }
  }, [nextPage, loading, loadingMore, preloadedPages, getBlogPageWithSize]);

  const callbackFunction = useCallback(() => {
    if (!loading && !loadingMore && nextPage) {
      setLoadingMore(true);
      
      getBlogPageWithSize({
        page: nextPage,
        size: 10,
      });
      
      
    }
  }, [getBlogPageWithSize, nextPage, loading, loadingMore]);

  // Create Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const blogId = entry.target.dataset.blogId;
            if (blogId) {
              setVisibleBlogs(prev => {
                if (!prev.has(blogId)) {
                  return new Set([...prev, blogId]);
                }
                return prev;
              });
            }
          }
        });
      },
      {
        rootMargin: '100px', // Increased from 50px to load earlier
        threshold: 0.1
      }
    );

    observerRef.current = observer;
    
    return () => {
      observer.disconnect();
    };
  }, []);

  // Load first few images immediately (critical content)
  useEffect(() => {
    if (blogPageWithSize.length > 0) {
      const criticalBlogIds = blogPageWithSize.slice(0, 4).map(blog => blog._id).filter(Boolean);
      if (criticalBlogIds.length > 0) {
        setVisibleBlogs(prev => {
          const newVisible = new Set([...prev, ...criticalBlogIds]);
          return newVisible;
        });
      }
    }
  }, [blogPageWithSize.length]);

  // Track scroll for preloading
  useEffect(() => {
    const throttledPreloading = throttle(handlePreloading, 200);
    window.addEventListener('scroll', throttledPreloading, { passive: true });
    return () => window.removeEventListener('scroll', throttledPreloading);
  }, [handlePreloading]);

  // Simple throttle function
  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  
  useEffect(() => {
    setLoadingMore(false);
    
    if (blogPageWithSize.length > 0) {
      
    }
  }, [blogPageWithSize.length]);
  
  useBottomScrollListener(callbackFunction);

  // Pre-compute which blogs need presigned URLs to avoid string checks on every render
  const blogsWithUrlInfo = useMemo(() => {
    return blogPageWithSize.map(blog => ({
      ...blog,
      needsPresignedUrl: blog?.coverPhoto && isS3Url(blog.coverPhoto),
      hasValidUrl: Boolean(blog?.coverPhoto)
    }));
  }, [blogPageWithSize, isS3Url]);

  // Lazy loading with Intersection Observer - only load URLs for visible blogs
  useEffect(() => {
    let isMounted = true;

    const fetchPresignedUrls = async () => {
      // Only fetch URLs for visible blogs that don't already have them cached
      const blogsNeedingUrls = blogsWithUrlInfo.filter(blog => 
        blog?._id && 
        visibleBlogs.has(blog._id) && // Only visible blogs
        !presignedUrls[blog._id] && 
        blog.hasValidUrl
      );

      if (blogsNeedingUrls.length === 0) return;

      const newUrls = {};
      
      // Process each blog individually to avoid blocking
      for (const blog of blogsNeedingUrls) {
        if (!isMounted) break;
        try {
          if (blog.needsPresignedUrl) {
            const url = await getPresignedUrlView(blog.coverPhoto);
            newUrls[blog._id] = url;
            
            
          } else {
            newUrls[blog._id] = blog.coverPhoto;
            
            
          }
          
          // Update URLs immediately for each blog to show images as they load
          if (isMounted) {
            setPresignedUrls(prevUrls => ({ 
              ...prevUrls, 
              [blog._id]: newUrls[blog._id] 
            }));
          }
          
        } catch (error) {
          console.error(`Failed to fetch presigned URL for blog ${blog._id}:`, error);
          newUrls[blog._id] = blog.coverPhoto;
          
          
          
          // Set fallback URL even on error
          if (isMounted) {
            setPresignedUrls(prevUrls => ({ 
              ...prevUrls, 
              [blog._id]: blog.coverPhoto 
            }));
          }
        }
      }
      
      
    };

    if (blogsWithUrlInfo.length > 0 && visibleBlogs.size > 0) {
      fetchPresignedUrls();
    }
    
    return () => {
      isMounted = false;
    };
  }, [visibleBlogs, blogsWithUrlInfo]); // Removed presignedUrls from dependencies to prevent loops

  // Observe blog cards with Intersection Observer
  const observeBlogCard = useCallback((element, blogId) => {
    const observer = observerRef.current;
    if (observer && element && blogId) {
      element.dataset.blogId = blogId;
      observer.observe(element);
      blogCardsRef.current.set(blogId, element);
    }
  }, []);

  // Cleanup observer when component unmounts
  useEffect(() => {
    return () => {
      const observer = observerRef.current;
      if (observer) {
        observer.disconnect();
      }
      blogCardsRef.current.clear();
    };
  }, []);

  
  return (
    <div className="news-container">
      {blogsWithUrlInfo.map((blog, i) => {
        return (
          <div
            className="vertical-news fade-in-item"
            style={{
              backgroundImage: presignedUrls[blog._id] ? `url(${presignedUrls[blog._id]})` : "none",
              animationDelay: `${i * 0.1}s`
            }}
            key={blog._id || i}
            ref={(element) => {
              if (element && blog._id) {
                observeBlogCard(element, blog._id);
              }
            }}
            onMouseEnter={() => {
              preloadArticle(blog._id, blog.coverPhoto);
            }}
          >
            <div className={`upper-vertical-news ${i % 2 === 0 && "blue"}`}>
              <h4 className={`title-news ${i % 2 === 1 && "blue"}`} style={{ textTransform: "uppercase" }}>
                {blog.title}
              </h4>
              <Link 
                to={`/article/${blog._id}`} 
                className={`read-more ${i % 2 === 1 && "blue"}`} 
                style={{ opacity: window.innerWidth < 900 && "1" }}
                onMouseEnter={() => {
                  // Additional preload trigger on link hover
                  preloadArticle(blog._id, blog.coverPhoto);
                }}
              >
                <div style={{ display: "flex" }}>
                  Прочети повече <div className={`arrowTab ${i % 2 === 1 ? "selectedBlue" : "selected"}`} style={{ height: "31px", marginLeft: window.innerWidth > 900 ? "8%" : "4%", marginTop: window.innerWidth > 900 ? "0.3%" : window.innerWidth < 900 && window.innerWidth > 760 ? "-0.4%" : "0%" }}></div>
                </div>
              </Link>
            </div>
          </div>
        );
      })}
      
      {/* Loading more indicator */}
      {loadingMore && (
        <div className="loading-more">
          <div className="loading-spinner-small">
            <div className="spinner-small"></div>
            <p>Зареждане на още статии...</p>
          </div>
        </div>
      )}
      
      {/* Error indicator for infinite scroll */}
      {error && blogPageWithSize.length > 0 && (
        <div className="loading-error">
          <p>Грешка при зареждане на още статии</p>
          <button 
            onClick={() => getBlogPageWithSize({ page: nextPage, size: 10 })}
            className="retry-button-small"
          >
            Опитай отново
          </button>
        </div>
      )}
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(NewsComponent);
